import type { DocumentStatus } from "@esi/shared";
import { projectHomePath } from "../router.tsx";
import type { ItemNode } from "./knowledge-tree.ts";

/** The Sources tab's status filter (`?status=`): files failed, or still processing. */
export const SOURCE_STATUS_FILTERS = ["failed", "processing"] as const;
export type SourceStatusFilter = (typeof SOURCE_STATUS_FILTERS)[number];

/** The statuses each filter keeps: an upload not yet indexed counts as processing. */
const STATUSES: Record<SourceStatusFilter, readonly DocumentStatus[]> = {
  failed: ["failed"],
  processing: ["uploading", "processing"],
};

/** The filter in a query string, or null for none or an unknown value. */
export function sourceStatusFilter(value: string | null | undefined): SourceStatusFilter | null {
  return SOURCE_STATUS_FILTERS.find((f) => f === value) ?? null;
}

/**
 * The Sources tab filtered to one status; `mine` (`&uploader=me`) keeps the viewer's own uploads,
 * as Overview's Failed figure counts them for those who may not manage every file.
 */
export const sourcesStatusPath = (projectId: string, filter: SourceStatusFilter, mine = false) =>
  `${projectHomePath(projectId, "files")}?status=${filter}${mine ? "&uploader=me" : ""}`;

/**
 * Whether a tree item stays under the filter: only files, by their status. It narrows what the
 * server already returned for this viewer; it never reveals anything.
 */
export const acceptsStatus =
  (filter: SourceStatusFilter, uploader: string | null = null) =>
  (item: ItemNode): boolean =>
    item.type === "file" &&
    STATUSES[filter].includes(item.document.status) &&
    (uploader === null || item.document.uploadedBy.toLowerCase() === uploader.toLowerCase());
